import { useEffect, useRef } from 'react';

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@500&display=swap');

  .twm-overlay {
    position: fixed;
    inset: 0;
    background: rgba(10, 10, 12, 0.55);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    font-family: 'DM Sans', sans-serif;
    animation: twm-fadeIn 0.18s ease;
  }

  @keyframes twm-fadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }

  @keyframes twm-slideUp {
    from { opacity: 0; transform: translateY(18px) scale(0.97); }
    to   { opacity: 1; transform: translateY(0)    scale(1);    }
  }

  .twm-modal {
    background: #fff;
    border-radius: 18px;
    width: 100%;
    max-width: 440px;
    margin: 16px;
    box-shadow:
      0 0 0 1px rgba(0,0,0,0.06),
      0 24px 64px rgba(0,0,0,0.18);
    animation: twm-slideUp 0.22s cubic-bezier(0.34, 1.3, 0.64, 1);
    overflow: hidden;
  }

  .twm-stripe {
    height: 4px;
    background: linear-gradient(90deg, #F8B71F 0%, #f59e0b 50%, #F8B71F 100%);
    background-size: 200% 100%;
    animation: twm-shimmer 2.5s linear infinite;
  }

  @keyframes twm-shimmer {
    0%   { background-position: 100% 0; }
    100% { background-position: -100% 0; }
  }

  .twm-body {
    padding: 28px 28px 24px;
  }

  .twm-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 14px;
    background: #fffbeb;
    border: 1.5px solid #fde68a;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 18px;
    flex-shrink: 0;
  }

  .twm-icon-svg {
    width: 24px;
    height: 24px;
    color: #d97706;
  }

  .twm-title {
    font-size: 16px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 8px;
    letter-spacing: -0.01em;
  }

  .twm-desc {
    font-size: 13.5px;
    color: #6b7280;
    line-height: 1.6;
    margin: 0 0 4px;
  }

  .twm-tracking-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin: 12px 0 0;
    padding: 7px 14px;
    background: #f9fafb;
    border: 1.5px solid #e5e7eb;
    border-radius: 999px;
    font-family: 'DM Mono', monospace;
    font-size: 13px;
    font-weight: 500;
    color: #1a1a1a;
    letter-spacing: 0.04em;
  }

  .twm-tracking-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #F8B71F;
    flex-shrink: 0;
  }

  .twm-divider {
    height: 1px;
    background: #f3f4f6;
    margin: 20px 0 0;
  }

  .twm-footer {
    padding: 16px 28px 24px;
    display: flex;
    gap: 10px;
    justify-content: flex-end;
  }

  .twm-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    border-radius: 10px;
    padding: 10px 20px;
    font-family: 'DM Sans', sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
    outline: none;
    white-space: nowrap;
  }

  .twm-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .twm-btn-cancel {
    background: #f3f4f6;
    color: #374151;
  }

  .twm-btn-cancel:hover:not(:disabled) {
    background: #e5e7eb;
  }

  .twm-btn-confirm {
    background: #1a1a1a;
    color: #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.15);
  }

  .twm-btn-confirm:hover:not(:disabled) {
    background: #111;
    box-shadow: 0 4px 12px rgba(0,0,0,0.22);
    transform: translateY(-1px);
  }

  .twm-btn-confirm:active:not(:disabled) {
    transform: translateY(0);
  }

  .twm-spinner {
    width: 13px;
    height: 13px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: twm-spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  @keyframes twm-spin {
    to { transform: rotate(360deg); }
  }
`;

// ── Types ─────────────────────────────────────────────────────────────────────

interface TrackingWarningModalProps {
  newTracking: string;
  onConfirm: () => void;
  onCancel: () => void;
  isSaving: boolean;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function TrackingWarningModal({
  newTracking,
  onConfirm,
  onCancel,
  isSaving,
}: TrackingWarningModalProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSaving) onCancel();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isSaving, onCancel]);

  // Focus cancel button on open
  useEffect(() => {
    cancelRef.current?.focus();
  }, []);

  // Block body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleOverlayMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSaving) onCancel();
  };

  return (
    <>
      <style>{styles}</style>

      <div
        className="twm-overlay"
        onMouseDown={handleOverlayMouseDown}
        role="dialog"
        aria-modal="true"
        aria-labelledby="twm-title"
        aria-describedby="twm-desc"
      >
        <div className="twm-modal">

          {/* Amber shimmer stripe */}
          <div className="twm-stripe" />

          <div className="twm-body">

            {/* Warning icon */}
            <div className="twm-icon-wrap" aria-hidden="true">
              <svg
                className="twm-icon-svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
                />
              </svg>
            </div>

            <h2 className="twm-title" id="twm-title">
              One-time action — please confirm
            </h2>

            <p className="twm-desc" id="twm-desc">
              Once assigned, this tracking number{' '}
              <strong>cannot be changed again</strong>. Please double-check
              the number below before proceeding.
            </p>

            <div className="twm-tracking-pill">
              <span className="twm-tracking-dot" />
              {newTracking}
            </div>
          </div>

          <div className="twm-divider" />

          <div className="twm-footer">
            <button
              ref={cancelRef}
              className="twm-btn twm-btn-cancel"
              onClick={onCancel}
              disabled={isSaving}
              type="button"
            >
              Cancel
            </button>

            <button
              className="twm-btn twm-btn-confirm"
              onClick={onConfirm}
              disabled={isSaving}
              type="button"
            >
              {isSaving ? (
                <>
                  <span className="twm-spinner" />
                  Saving…
                </>
              ) : (
                <>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    aria-hidden="true"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="m4.5 12.75 6 6 9-13.5"
                    />
                  </svg>
                  Yes, Confirm
                </>
              )}
            </button>
          </div>

        </div>
      </div>
    </>
  );
}